<template>
  <div class="common-layout">
    <el-container class="stage-two">
      <el-aside id="aside-map" class="map-container">
        <GMapMap
          :zoom="19"
          :map-type-id="'satellite'"
          :center="{ lat: address.lat, lng: address.long }"
          :options="{
            zoomControl: false,
            mapTypeControl: showTypeControls,
            scaleControl: false,
            streetViewControl: false,
            rotateControl: true,
            fullscreenControl: false,
          }"
          v-if="address !== undefined"
          @click="mapClicked"
        >
          <GMapMarker
            :key="index"
            v-for="(m, index) in markers"
            :position="m.position"
            :draggable="true"
            :icon="{
              url: iconUrl,
              anchor: { x: 16, y: 16 },
            }"
            @drag="markerDragged($event, index)"
            @dragend="markerDragStop($event, index)"
          />
          <GMapPolygon
            :key="index"
            v-for="(polygon, index) in polygons"
            :paths="polygon"
            :deepWatch="true"
            :options="{
              strokeColor: '#FAB219',
              strokeOpacity: 1,
              strokeWeight: 4,
              fillColor: '#FAB219',
              fillOpacity: 0.1,
            }"
          />
        </GMapMap>
        <GMapAutocomplete
          @place_changed="setPlace"
          :placeholder="address.address"
          :options="{
            componentRestrictions: {
              country: ['fi', 'ax', 'ee'],
            },
          }"
        >
        </GMapAutocomplete>
      </el-aside>
      <el-main>
        <StageTwoOne
          @manualAreaChange="manualAreaChange"
          @deletePolygon="deletePolygon"
          :polygons="polygons"
          v-if="stage === 2"
        />
        <StageTwoTwo
          @polygonChecked="polygonChecked"
          @updateCurrentStats="updateCurrentStats"
          :calculationData="calculationData"
          :polygons="polygons"
          :checked="checked"
          :calculatedPrice="calculatedPrice"
          v-if="stage === 3"
        />
        <StageTwoBudgetCalculator
          v-if="stage === 2.5"
          @emitBudget="budgetEmitted"
          :calculatedPrice="calculatedPrice"
          :calculationData="calculationData"
          :budgetValues="budgetValues"
          :numberOfRoofs="numberOfRoofs"
        />
        <StageTwoThree
            v-if="stage === 4"
            :calculatedPrice="calculatedPrice"
            :calculationData="calculationData"
            :budget="budget"
            :showBudget="showBudget"
            @emitResultScreenshot="screenshotEmitted"
        />
        <ContactForm
          v-if="stage === 5"
          :form-data="formData"
          :validate="validate"
        />
        <div v-if="stage === 4 && showBudget === 'true' && calculationData.modulePower < 22">
          <span>Tilaa yhteenveto sähköpostiisi saadaksesi myös kustannusarvion.</span>
        </div>
        <div class="nav-bottom">
          <el-button v-if="stage !== undefined && (stage % 0.5) === 0 && (stage % 1) !== 0" @click="changeStage(-0.5)">
            <img
              class="navigator-icon"
              src="/src/assets/icons/circle-arrow-left.svg"
            />
            {{ $t("general.back") }}
          </el-button>
          <el-button v-else-if="showBudget === 'true' && stage === 3" @click="changeStage(-0.5)">
            <img
              class="navigator-icon"
              src="/src/assets/icons/circle-arrow-left.svg"
            />
            {{ $t("general.back") }}
          </el-button>
          <el-button v-else @click="changeStage(-1)">
            <img
              class="navigator-icon"
              src="/src/assets/icons/circle-arrow-left.svg"
            />
            {{ $t("general.back") }}
          </el-button>
          <template v-if="stage === 4">
            <el-button @click="printResults">
              <img class="navigator-icon" src="/src/assets/icons/printer.svg" />
              {{ $t("stageTwoThree.printResults") }}
            </el-button>
            <el-button @click="changeStage(1)" :disabled="!screenshotTaken">
              {{ $t('stageTwoThree.orderSummary') }}
              <img
                class="navigator-icon"
                src="/src/assets/icons/circle-arrow-right.svg"
              />
            </el-button>
          </template>
          <el-button v-else-if="stage === 3" @click="changeStage(1)">
            {{ $t("general.viewResults") }}
            <img
              class="navigator-icon"
              src="/src/assets/icons/circle-arrow-right.svg"
            />
          </el-button>
          <el-button v-else-if="stage === 2 && showBudget === 'true'" @click="changeStage(0.5)">
            {{ $t("general.continue") }}
            <img
              class="navigator-icon"
              src="/src/assets/icons/circle-arrow-right.svg"
            />
          </el-button>
          <el-button v-else-if="stage === 2.5" :disabled="disableBudgetButton" @click="changeStage(0.5)">
            {{ $t("general.continue") }}
            <img
              class="navigator-icon"
              src="/src/assets/icons/circle-arrow-right.svg"
            />
          </el-button>
          <el-button v-else-if="stage === 5" @click="submitForm">
            {{ $t("general.send") }}
            <img
              class="navigator-icon"
              src="/src/assets/icons/circle-arrow-right.svg"
            />
          </el-button>
          <el-button v-else @click="changeStage(1)">
            {{ $t("general.continue") }}
            <img
              class="navigator-icon"
              src="/src/assets/icons/circle-arrow-right.svg"
            />
          </el-button>
        </div>
      </el-main>
    </el-container>
  </div>
</template>

<script lang="ts" setup>
import StageTwoOne from "@/components/StageTwo-One.vue";
import StageTwoTwo from "@/components/StageTwo-Two.vue";
import StageTwoThree from "@/components/StageTwo-Three.vue";
import ContactForm from "@/components/ContactForm.vue";
import { computed, type PropType, reactive, ref } from "vue";
import { get } from "@vueuse/core";
import iconUrl from "../assets/icons/dot-orange.png";
import type { google } from "google-maps";
import type { AddressLocation, CalculationData, FormData, Marker, Polygon } from "@/types";
import { useI18n } from "vue-i18n";
import { postData } from "@/http-gf";
import StageTwoBudgetCalculator from "@/components/StageTwo-BudgetCalculator.vue";
import modifierData from "@/components/modifiers.json";


const props = defineProps({
  initialAddress: {
    type: Object as PropType<AddressLocation>,
    required: true,
  },
  stage: Number,
  showBudget: String,
});

const modifiers: any = modifierData;

let validate = ref<Array<string>>([]);

const emit = defineEmits(["changeStage"]);

const { t, locale } = useI18n();

const showTypeControls = computed(() => {
  return locale.value === "et";
});

const disableBudgetButton = computed(() => {
  return budget.value[1] !== 1;
})

const address = ref<AddressLocation>(
  props.initialAddress as unknown as AddressLocation
);

const priceEstimate = ref<Number>(0);

const formData = ref<FormData>({
  first: "",
  last: "",
  phone: "",
  email: "",
  address: get(address).address as string,
  postal: get(address).postNumber as string,
  city: get(address).postAddress as string,
  panelAmount: "",
  consumption: "",
  results: "",
  electricityPrice: "",
  locale: "fi",
  agreement: true,
});

// Variables used in budgetCalculator, defined here to make them not reset when switching stage.
const budgetValues = ref(["","","",""]);
const numberOfRoofs = ref(1);

const resultScreenshot = ref<string | null>(null);
const screenshotTaken = ref<boolean>(false);

const markers = ref<Marker[]>([]);
const polygons = ref<Polygon[][]>([]);
const checked = ref<boolean[]>([]);
const budget = ref<number[]>([0,0]);
const calculationData = reactive<CalculationData>({
  costPerkWh: 15,
  currentYearlyConsumption: 15000,
  currentYearlyConsumptionMonthly: [2250, 2100, 1350, 1050, 675, 525, 450, 525, 975, 1275, 1650, 2175], // This array sums up to 15000. Its yearly consumption per month.
  EVDriveLength: 0,
  estimatedPrice: 0,
  indicativeSavings: 0,
  inverterPower: 0,
  modulePower: 0,
  modules: 0,
  saunasHeated: 0,
  savedCO2: 0,
  surplusEarnings: 0,
  treeBinding: 0,
  yearlyProd: 0,
  totalArea: 0,
});

function updatePriceEstimate(x: number) {
  console.log(x);
  priceEstimate.value = x;
}

function submitForm(): void {
  const formId = 3;
  if (validateForm()) {
    const data = {
      input_1: formData.value.first,
      input_3: formData.value.last,
      input_4: formData.value.phone,
      input_5: formData.value.email,
      input_6: formData.value.address,
      input_7: formData.value.postal,
      input_8: formData.value.city,
      input_9: calculationData.modules,
      input_10: calculationData.currentYearlyConsumption,
      input_11: cf(
        t("stageTwoThree.yearlyProd1") +
          " " +
          Math.round(calculationData.yearlyProd) +
          " kWh " +
          t("stageTwoThree.yearlyProd2") +
          "."
      ),
      input_12: calculationData.costPerkWh,
      input_13: locale.value,
      input_14: formData.value.agreement,
      input_15: cf(
        t("stageTwoThree.CO2Saved1") +
          " " +
          calculationData.savedCO2 +
          " kg/CO² " +
          t("stageTwoThree.CO2Saved2") +
          "."
      ),
      input_16: cf(
        t("stageTwoThree.estimatedEnergySavings") +
          " " +
          calculationData.indicativeSavings +
          " " +
          t("stageTwoThree.moneyPerWeek") +
          "."
      ),
      input_17: cf(
        t("stageTwoThree.estimatedSurplus") +
          " " +
          calculationData.surplusEarnings +
          " " +
          t("stageTwoThree.moneyPerWeek") +
          "."
      ),
      input_18: resultScreenshot.value
    };

    postData(`/forms/${formId}/submissions`, data).then(() => {
      changeStage(1);
    });
  }
}

function validateForm(): boolean {
  validate.value.length = 0;
  let valid = true;
  if (!formData.value.first) {
    validate.value.push("first");
    valid = false;
  }
  if (!formData.value.last) {
    validate.value.push("last");
    valid = false;
  }
  if (!formData.value.phone) {
    validate.value.push("phone");
    valid = false;
  }
  if (!formData.value.email) {
    validate.value.push("email");
    valid = false;
  }
  if (!formData.value.postal) {
    validate.value.push("postal");
    valid = false;
  }
  return valid;
}

function printResults(): void {
  window.print();
}

// Convert first letter to upper case
function cf(s: string): string {
  return s[0].toUpperCase() + s.substring(1);
}

// Is executed when map is clicked and marker is attempted to be placed
function mapClicked(e: any) {
  // Add marker to marker array
  get(markers).push({
    position: {
      lat: e.latLng?.lat(),
      lng: e.latLng?.lng(),
    },
  });

  // If enough markers (4)
  if (get(markers).length - get(polygons).length * 4 >= 4) {
    // Create polygon out of markers
    get(polygons).push([
      {
        lat: get(markers)[get(polygons).length * 4].position.lat,
        lng: get(markers)[get(polygons).length * 4].position.lng,
      },
      {
        lat: get(markers)[1 + get(polygons).length * 4].position.lat,
        lng: get(markers)[1 + get(polygons).length * 4].position.lng,
      },
      {
        lat: get(markers)[2 + get(polygons).length * 4].position.lat,
        lng: get(markers)[2 + get(polygons).length * 4].position.lng,
      },
      {
        lat: get(markers)[3 + get(polygons).length * 4].position.lat,
        lng: get(markers)[3 + get(polygons).length * 4].position.lng,
      },
    ]);
    get(checked).push(true);

    // Recalculates area
    calculateTotalArea();
  }
}

function updateCurrentStats({
  currentYearlyConsumption,
  currentCostPerkWh,
}: {
  currentYearlyConsumption: number;
  currentCostPerkWh: number;
}) {
  calculationData.currentYearlyConsumption = currentYearlyConsumption;
  calculationData.costPerkWh = currentCostPerkWh;
  calculateData();
}

// Is executed when new place is inserted into autocomplete input
function setPlace(e: google.maps.places.PlaceResult) {
  // Set new address (place)
  if (!e || !e.geometry || !e.address_components) return;
  let postCode = "";

  e.address_components.forEach((e) => {
    if (e.types[0] === "postal_code") {
      postCode = e.long_name;
    }
  });

  get(address).long = e.geometry.location.lng();
  get(address).lat = e.geometry.location.lat();
  get(address).postAddress = e.address_components[2].long_name;
  get(address).address = e.name;
  get(address).postNumber = postCode;

  get(formData).address = get(address).address as string;
  get(formData).postal = get(address).postNumber as string;
  get(formData).city = get(address).postAddress as string;
}

// When marker is dragged, resize polygon
function markerDragged(e: any, index: number) {
  // If polygon exists with this marker, adjust position.
  const polygonIndex = Math.floor(index / 4);
  if (get(polygons).length >= polygonIndex + 1) {
    get(polygons)[polygonIndex][index % 4].lng = e.latLng.lng();
    get(polygons)[polygonIndex][index % 4].lat = e.latLng.lat();

    // Recalculates area
    calculateTotalArea();
  }
}

// When marker drag i stopped, update marker array values (This is messy during @drag, so do it for @dragend)
function markerDragStop(e: any, index: number) {
  get(markers)[index].position.lng = e.latLng.lng();
  get(markers)[index].position.lat = e.latLng.lat();
}

// This is emitted from polygon controller. Deleted markers & polygon associated with specific polygon
function deletePolygon(index: number) {
  get(polygons).splice(index, 1);
  get(markers).splice(index * 4, 4);
  get(checked).splice(index, 1);

  // Recalculates area
  calculateTotalArea();
}

// Executed when next/previous buttons are clicked
function changeStage(s: number) {
  console.log("Changin stage");
  console.log(s);
  // Add verification if change can be changed.
  switch (props.stage) {
    case 2:
      if (s === 1) {
        // only allow stage incrementation if area is manually set, or if at least one polygon is selected
        if (calculationData.totalArea > 0 || get(polygons).length > 0) {
          calculateData(); // Calculate data to fill Object with values
          emit("changeStage", s);
        } else {
          console.log("Total area not set or no polygons given!");
        }
      } else {
        emit("changeStage", s);
      }
      break;

    case 3:
      if (s === 1) {
        // only allow stage incrementation if area is more than zero
        if (calculationData.totalArea > 0) {
          emit("changeStage", s);
        } else {
          console.log("Total area not set!");
        }
      } else {
        emit("changeStage", s);
      }
      break;
    case 4:
      if (s === 1) {
        // only allow stage incrementation if area is more than zero
        if (calculationData.totalArea > 0) {
          emit("changeStage", s);
        } else {
          console.log("Total area not set!");
        }
      } else {
        emit("changeStage", s);
      }
      break;
    case 5:
      if (s === 1) {
        emit("changeStage", s);
      } else {
        emit("changeStage", s);
      }
      break;
    case 2.5:
      emit("changeStage", s);
      break;
    default:
      console.log("Unsupport stage switch encountered at stage:");
      console.log(props.stage);
      if (s === 1) {
        console.log(
          "Attempted to increment stage from unsupported stage, breaking."
        );
      } else {
        console.log(
          "Attempted to decrement stage from unsupported stage, decrementing."
        );
        emit("changeStage", s);
      }
  }
}

// Calculates total area, will be executed when polygons change or when a polygon is "unchecked"
function calculateTotalArea() {
  let area = 0;

  for (let i = 0; i < get(checked).length; i++) {
    if (get(checked)[i]) {
      // Use google computeArea function to calculate area of polygon
      const convertedLatLng = get(polygons)[i].map((p) => {
        return new google.maps.LatLng(p.lat, p.lng);
      });

      area = area + google.maps.geometry.spherical.computeArea(convertedLatLng);
    }
  }

  calculationData.totalArea = area;
  calculateData();
}

const productionCoefficients = {
  fi: 870,
  et: 900,
  en: 870,
};

const co2Coefficients = {
  fi: 0.55,
  et: 0.579,
  en: 0.55,
};

function getCo2Coefficient() {
  if (locale.value in co2Coefficients) {
    return co2Coefficients[
      locale.value as keyof typeof co2Coefficients
      ];
  }
  return productionCoefficients["fi"];
}

function getProductionCoefficient() {
  if (locale.value in productionCoefficients) {
    return productionCoefficients[
      locale.value as keyof typeof productionCoefficients
    ];
  }
  return productionCoefficients["fi"];
}

const gridPriceCoefficient = 50;
const gridPrice = 8;
const earningsRatio = 0.7;

// Calculates data
function calculateData() {
  const singleModulePower = locale.value === "et" ? 430 : 420;
  calculationData.modules = Math.floor(calculationData.totalArea / 2.5);
  calculationData.modulePower = (calculationData.modules * singleModulePower) / 1000;
  calculationData.inverterPower = Math.floor(calculationData.modulePower / 1.2);
  calculationData.yearlyProd = calculationData.modulePower * getProductionCoefficient() * 0.8; // 870 in Finland, this variable will change per country
  calculationData.savedCO2 = Math.round(getCo2Coefficient() * calculationData.yearlyProd);
  calculationData.treeBinding = Math.round(calculationData.savedCO2 / 20); // Math.round(calculationData.modulePower * 7.0);
  calculationData.saunasHeated = Math.round(calculationData.yearlyProd / 12);
  calculationData.EVDriveLength = Math.round(
    (calculationData.yearlyProd / 20) * 100
  );
  calculationData.surplusEarnings = Math.floor( (calculationData.costPerkWh * calculationData.yearlyProd - gridPrice) * earningsRatio / 100 );  
  calculationData.indicativeSavings = Math.floor( (calculationData.costPerkWh * calculationData.yearlyProd) * (1-earningsRatio) / 100 );
  calculationData.estimatedPrice = -1; // @todo
  calculationData.currentYearlyConsumptionMonthly = [Math.round(calculationData.currentYearlyConsumption*.15),
    Math.round(calculationData.currentYearlyConsumption*0.14),
    Math.round(calculationData.currentYearlyConsumption*0.09),
    Math.round(calculationData.currentYearlyConsumption*0.07),
    Math.round(calculationData.currentYearlyConsumption*0.045),
    Math.round(calculationData.currentYearlyConsumption*0.035),
    Math.round(calculationData.currentYearlyConsumption*0.03),
    Math.round(calculationData.currentYearlyConsumption*0.035),
    Math.round(calculationData.currentYearlyConsumption*0.065),
    Math.round(calculationData.currentYearlyConsumption*0.085),
    Math.round(calculationData.currentYearlyConsumption*0.11),
    Math.round(calculationData.currentYearlyConsumption*0.145)];
}

// Total area was manually inserted
function manualAreaChange(area: number) {
  calculationData.totalArea = area;
  calculateData();
}

// Sets checked array by index
function polygonChecked(i: number, v: boolean) {
  get(checked)[i] = v;

  // Recalculates area
  calculateTotalArea();
}

function budgetEmitted(x: any) {
  budget.value = x[0];
  numberOfRoofs.value = x[1];
}

// When screenshot is emitted.
function screenshotEmitted(x: [string, boolean]) {
  resultScreenshot.value = x[0];
  screenshotTaken.value = true // Could also be x[1], but when emitted, this should always be true, even if x[0] is null.
}

// This is the polynomial function that calculates the price range.
const priceRange = computed(() => {
  let x;
  const modules = calculationData.modules;
  if (calculationData.modules <= 0) return [0, 0];
  if (modules > 0 && modules <= 60) {
    x = modules - 11;
    return [
      -0.0543 * Math.pow(x, 3) + 4.709 * Math.pow(x, 2) + 188.04 * x + 6971.7,
      -0.0259 * Math.pow(x, 3) + 2.0805 * Math.pow(x, 2) + 387.52 * x + 10078,
    ];
  }
  if (modules > 60 && modules <= 118) {
    x = modules - 60;
    return [237.54 * x + 21527, 409.9 * x + 31308];
  }
  if (modules > 118 && modules <= 237) {
    x = modules - 118;
    return [210.36 * x + 35588, 378.36 * x + 55412];
  }
  if (modules > 237 && modules <= 356) {
    x = modules - 237;
    return [189.17 * x + 60193, 357.17 * x + 100009];
  }
  if (modules > 356 && modules <= 475) {
    x = modules - 356;
    return [147.17 * x + 82726, 315.18 * x + 142533];
  }
  if (modules > 475) {
    return [modules * 0.5 * 420, modules * 0.9 * 420];
  }
  return [0, 0];
});

const calculatedPrice = computed(() => {
  // Separate price if module amount exceeds 476
  if (calculationData.modules > 476) {
    const singleModulePower = locale.value === "et" ? 430 : 420;
    const p = calculationData.modules * 0.57 * singleModulePower;
    return [Math.round(p / 100) * 100, Math.round(p / 100) * 100]
  }
  let modifier = 1;
  if (calculationData.modules in modifiers){
    modifier = modifiers[calculationData.modules];
  }

  if (!priceRange.value) return [0, 0];
  return priceRange.value.map((x) => (Math.round((x* modifier) / 100) * 100));
});


</script>
